import { PAYMENT_METHOD_LABEL, PAYMENT_TAB_LABEL, PaymentTab, type PaymentList } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { usePageName } from "../../components/BackButton.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText, Help } from "../../components/ui.jsx";
import { useLabels } from "../../labels.js";
import { md, yen } from "../../lib/num.js";

export const PAYMENTS_KEY = ["payments"] as const;
const TABS: PaymentTab[] = ["unallocated", "month", "all"];

/**
 * 入金の一覧（部品 payments、営業力 E1、../eigyouryoku/docs/08 §5）。
 *   タブ：まだ消し込んでいない（既定）／今月／すべて。まだ消し込んでいない入金はどのタブでも一番上（営業力ルール9）
 */
export function PaymentsPage() {
  const L = useLabels();
  usePageName("入金");
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const parsed = PaymentTab.safeParse(sp.get("tab"));
  const tab: PaymentTab = parsed.success ? parsed.data : "unallocated";
  const q = useQuery({ queryKey: [...PAYMENTS_KEY, tab], queryFn: () => api<PaymentList>("GET", `/payments?tab=${tab}`) });
  const d = q.data;
  const setTab = (k: PaymentTab) => setSp(k === "unallocated" ? {} : { tab: k }, { replace: true });
  return (
    <>
      <div className="hrow">
        <h1>
          入金
          <Help testId="help-payments" text={`${L.customer}から入ったお金を登録して、出した請求書に当てます（消込）。1回の振込で何枚分でも当てられます。当てていない額は「まだ消し込んでいない」に残り続け、請求書を出したら、入金の画面の「修正」から当てられます。`} />
          {/* 消し込んでいない入金（#168：帯 → 見出しの右のチップ） */}
          {d && d.counts.unallocated > 0 ? (
            <span className="chip ai" data-testid="unallocated-band">
              未消込 {d.counts.unallocated}件 <span className="num">{yen(d.unallocatedTotal)}</span> 円
            </span>
          ) : null}
        </h1>
        <span className="sp" />
        <Link className="btn primary" to="/payments/new" data-testid="payment-new">
          <Icon k="plus" />
          入金登録
        </Link>
      </div>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      <ListTabs id="pl" label="入金を絞る" tabs={TABS.map((k) => ({ key: k, label: PAYMENT_TAB_LABEL[k], count: d ? d.counts[k] : "…" }))} value={tab} onChange={setTab} />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("pl", tab)}>
        <table className="list" data-testid="payments-table">
          <thead>
            <tr>
              <th>入金日</th>
              <th>{L.customer}</th>
              <th className="amt">入金の額</th>
              <th>方法</th>
              <th>当てた請求書</th>
              <th className="amt">まだ当てていない額</th>
            </tr>
          </thead>
          <tbody>
            {(d?.items ?? []).map((x) => (
              <tr key={x.id} className="r" tabIndex={0} onClick={() => nav(`/payments/${x.id}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/payments/${x.id}`)} data-testid="payment-row">
                <td className="num">{md(x.receivedOn)}</td>
                <td>
                  <b>{x.partnerName}</b>
                  {x.voided ? <span className="chip" style={{ marginLeft: 8 }}>消した</span> : null}
                  {x.hasReceipt ? <span className="chip" style={{ marginLeft: 8 }}>領収書あり</span> : null}
                </td>
                <td className="amt num">{yen(x.amount)} 円</td>
                <td>{PAYMENT_METHOD_LABEL[x.method]}</td>
                <td className="num">{x.allocatedTo || <span className="muted">まだ当てていない</span>}</td>
                <td className="amt num">{x.unallocated > 0 ? <b data-testid="payment-unallocated">{yen(x.unallocated)} 円</b> : <span className="muted">—</span>}</td>
              </tr>
            ))}
            {d && d.items.length === 0 ? (
              <tr className="emptyrows">
                <td colSpan={6}>
                  {tab === "unallocated" ? "まだ消し込んでいない入金はありません。" : tab === "month" ? "今月の入金はまだありません。" : "まだ入金の登録はありません。「入金登録」から始めます。"}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
